import {
    FovOption,
    FovSelect,
    FovModal,
    FovFieldError,
    FovInput,
    FovButton,
    VerticalSpacer,
} from 'components';
import { Fragment, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import * as yup from 'yup';
import { Formik, Field } from 'formik';
import { addTeamMember } from 'store/actions/teamData';
import { useCookies } from 'react-cookie';

const teamRoles = [ 'admin', 'member' ];

const ButtonContainer = styled('div')`
    display: inline-flex;
    width: 100%;
`;

const AddMemberForm = withTheme(props => {
    const { isVisible, onClose } = props;

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;
    const dispatch = useDispatch();
    const formikRef = useRef();
    const teamDataStore = useSelector(state => state.teamData);
    const teamId = useMemo(() => teamDataStore.teamId, [ teamDataStore.teamId ]);
    const allowedDomains = useMemo(() => teamDataStore.allowedDomains, [ teamDataStore.allowedDomains ]);

    const handleSubmit = useCallback(args => {
        const { values, setSubmitting } = args;
        const { username, domain, role } = values;
        dispatch(addTeamMember(token, teamId, username + domain, role))
            .then(() => {
                setSubmitting(false);
                onClose();
            })
            .catch(() => {
                setSubmitting(false);
                onClose();
            });
    }, []);

    const buildAllowedDomainOptions = () => {
        if (allowedDomains.length === 0) {
            return (
                <FovOption value='Please add an allowed domain in settings.'>
                    No domains found.
                </FovOption>
            );
        }

        return allowedDomains.map((domain, index) => (
           <FovOption
                key={index}
                value={domain}
            >
                {domain}
            </FovOption>
        ));
    };

    const buildTeamRoleOptions = () => {
        return teamRoles.map((role, index) => (
            <FovOption
                key={index}
                value={role}
            >
                {role}
            </FovOption>
        ));

    };

    const setRef = form => {
        if (!formikRef.current) {
            formikRef.current = form;
        }
    };

    const defaults = {
        username: '',
        domain: allowedDomains?.[0] || '',
        role: 'member',
    };

    const schema = yup.object().shape({
        username: yup.string().required('A username is required.'),
        domain: yup.string().required('A domain is required.'),
        role: yup.string().required('A role is required.'),
    });

    return (
        <FovModal
            title='Add a member'
            visible={isVisible}
            onClose={onClose}
        >
        <Fragment>
        <Formik
            initialValues={defaults}
            validationSchema={schema}
            onSubmit={(values, handlers) => {
                const { setSubmitting } = handlers;
                handleSubmit({ values, setSubmitting });
            }}
        >
        {form => (
            <Fragment>
            {setRef(form)}
            Username
            <Field type='text' name='username'>
            {data => (
                <FovInput
                    value={data.field.value}
                    {...data.field}
                    onChange={e => {
                        const newVal = e.target.value;
                        form.setFieldValue(data.field.name, newVal);

                    }}

                />

            )}
            </Field>
            <FovFieldError name='username' />

            Domain
            <Field type='select' name='domain'>
            {data => (
                <FovSelect
                    value={data.field.value}
                    placeholder={data.field.value}
                    disabled={allowedDomains.length === 0}
                    onChange={(e, newVal) => {
                        form.setFieldValue(data.field.name, newVal);
                    }}
                >
                    {buildAllowedDomainOptions()}
                </FovSelect>
            )}
            </Field>
            <FovFieldError name='domain'/>

            Role
            <Field type='select' name='role'>
            {data => (
                <FovSelect
                    value={data.field.value}
                    placeholder={data.field.value}
                    disabled={allowedDomains.length === 0}
                    onChange={(e, newVal) => {
                        form.setFieldValue(data.field.name, newVal);
                    }}
                >
                    {buildTeamRoleOptions()}
                </FovSelect>
            )}
            </Field>
            <FovFieldError name='domain'/>

            <VerticalSpacer size={25} />
            <ButtonContainer>
            <FovButton
                type='submit'
                disabled={form.isSubmitting}
                onClick={form.handleSubmit}
            >
            Add Member
            </FovButton>
            </ButtonContainer>
            </Fragment>
        )}
        </Formik>
        </Fragment>
        </FovModal>
    );

});

export { AddMemberForm };
